/**
 * viewerCosmetics.ts — DURABLE viewer-local cosmetics foundation.
 *
 * Single source of truth on the frontend for:
 *   • the cosmetic ID types (ViewerTileSkin / ViewerTableSkin),
 *   • the UI catalogs (id + label + thumbnail) the lobby renders,
 *   • safe normalization (mirrors the backend LobbyCosmetics allowlist), and
 *   • resolveEffectiveCosmetics — the ONE place appearance_mode ("host" vs
 *     "personal") is turned into the concrete table + tile skin a viewer sees.
 *
 * These are COSMETIC IDS ONLY. The artwork lives under Public/Assets and is
 * resolved by viewerTileSkinAssets.ts / viewerTableSkinAssets.ts. Keep the ID
 * lists in lockstep with the backend (app/models/lobby_cosmetics.rb).
 *
 * `ViewerTileSkin` used to live in TEMPORARY_STAGING_RED_TILE_TEST.ts; it is
 * durable and now permanently hosted here. Only the temporary email-based
 * SELECTION SOURCE was removed — this type and everything downstream stay.
 */

// ── Tile skins (face art) ──────────────────────────────────────────────────
export type ViewerTileSkin = 'classic' | 'red' | 'black' | 'rainbow';
export const VIEWER_TILE_SKINS: readonly ViewerTileSkin[] = ['classic', 'red', 'black', 'rainbow'] as const;
export const DEFAULT_TILE_SKIN: ViewerTileSkin = 'classic';

// ── Table skins (surface/felt art) ─────────────────────────────────────────
export type ViewerTableSkin = 'green_fabric' | 'green_leather' | 'black_leather';
export const VIEWER_TABLE_SKINS: readonly ViewerTableSkin[] = ['green_fabric', 'green_leather', 'black_leather'] as const;
export const DEFAULT_TABLE_SKIN: ViewerTableSkin = 'green_fabric';

// ── Appearance mode (UI label: "Host Skin" / "My Skin") ────────────────────
export type AppearanceMode = 'host' | 'personal';

export function normalizeTileSkin(id: string | null | undefined): ViewerTileSkin {
  return VIEWER_TILE_SKINS.includes(id as ViewerTileSkin) ? (id as ViewerTileSkin) : DEFAULT_TILE_SKIN;
}

export function normalizeTableSkin(id: string | null | undefined): ViewerTableSkin {
  return VIEWER_TABLE_SKINS.includes(id as ViewerTableSkin) ? (id as ViewerTableSkin) : DEFAULT_TABLE_SKIN;
}

// ── Catalog entries (drive the lobby's horizontal skin rows) ───────────────
export interface TileSkinCatalogEntry {
  id: ViewerTileSkin;
  label: string;
  /** Representative face (6-6) used as the catalog thumbnail + preview tile. */
  thumbnail: string;
}

export interface TableSkinCatalogEntry {
  id: ViewerTableSkin;
  label: string;
  /** Full table artwork, also used directly as the catalog thumbnail. */
  asset: string;
}

/** Representative face (6-6) for a tile skin — thumbnail + preview domino. */
export function tileSkinThumbnail(skin: ViewerTileSkin): string {
  return skin === 'classic'
    ? '/Assets/game/tiles/tiles_328x164/domino_6-6.png'
    : `/Assets/game/tiles/${skin}/tiles_328x164/domino_6-6.png`;
}

/** Full table artwork path for a table skin. */
export function tableSkinAsset(skin: ViewerTableSkin): string {
  return `/Assets/game/tables/${skin}.png`;
}

export const TILE_SKIN_CATALOG: readonly TileSkinCatalogEntry[] = [
  { id: 'classic', label: 'Classic', thumbnail: tileSkinThumbnail('classic') },
  { id: 'black',   label: 'Black',   thumbnail: tileSkinThumbnail('black') },
  { id: 'rainbow', label: 'Rainbow', thumbnail: tileSkinThumbnail('rainbow') },
  { id: 'red',     label: 'Red',     thumbnail: tileSkinThumbnail('red') },
] as const;

export const TABLE_SKIN_CATALOG: readonly TableSkinCatalogEntry[] = [
  { id: 'green_fabric',  label: 'Fabric',          asset: tableSkinAsset('green_fabric') },
  { id: 'green_leather', label: 'Leather (Green)', asset: tableSkinAsset('green_leather') },
  { id: 'black_leather', label: 'Leather (Black)', asset: tableSkinAsset('black_leather') },
] as const;

// ── Effective per-viewer cosmetics resolver ────────────────────────────────
// This mirrors the backend SessionUser#effective_* logic exactly so the lobby
// preview matches what the server will render in-game.

export interface EffectiveCosmetics {
  tableSkin: ViewerTableSkin;
  tileSkin: ViewerTileSkin;
}

export interface ResolveCosmeticsInput {
  /** The viewer's appearance mode. The host is always effectively "host". */
  appearanceMode: AppearanceMode;
  /** True when the viewer is the host (host is locked to the room theme). */
  isHost: boolean;
  /** The room's featured (host-chosen) skins. */
  hostTableSkin: string | null | undefined;
  hostTileSkin: string | null | undefined;
  /** This viewer's own personal selections (used only in "personal" mode). */
  selectedTableSkin: string | null | undefined;
  selectedTileSkin: string | null | undefined;
}

/**
 * Resolve the concrete skins a viewer sees. Host, or any guest in Host-Skin
 * mode → the room's featured theme; a guest in My-Skin (personal) mode → their
 * own selection. Everything is normalized so an unknown/missing id renders as
 * the safe default rather than breaking the view.
 */
/**
 * Boot-time cosmetics for GameScreen, derived from the authoritative game
 * state's viewer_cosmetics. Total function: a missing object, missing fields,
 * unknown ids, or a null (fetch-failed) input all resolve to safe defaults
 * (Classic dominoes + green_fabric table) so cosmetics can NEVER block Phaser
 * startup or reconnect.
 */
export function bootCosmeticsFromGameState(
  vc: { table_skin?: string | null; tile_skin?: string | null } | null | undefined
): { tileSkin: ViewerTileSkin; tableSkin: ViewerTableSkin } {
  return { tileSkin: normalizeTileSkin(vc?.tile_skin), tableSkin: normalizeTableSkin(vc?.table_skin) };
}

export type BootCosmetics = ReturnType<typeof bootCosmeticsFromGameState>;

/**
 * Identity-stable reducer for the GameScreen bootCosmetics state.
 *
 * The Phaser boot effect depends on the bootCosmetics OBJECT IDENTITY. The
 * per-round game-state refetch (gameId changes every round) used to call
 * setBootCosmetics with a fresh — but value-identical — object, which re-ran
 * the boot effect's cleanup and DESTROYED + REBOOTED the whole Phaser game on
 * every ordinary round transition (~1s black canvas while assets re-decoded).
 *
 * Returning the PREVIOUS reference when the resolved skins are unchanged makes
 * React bail out: no re-render from this state, no effect re-run, no reboot.
 * A genuinely different skin configuration still returns the new object, so a
 * different match's cosmetics still reboot Phaser exactly as intended.
 *
 * Use as: setBootCosmetics(prev => stableBootCosmetics(prev, next))
 */
export function stableBootCosmetics(
  prev: BootCosmetics | null,
  next: BootCosmetics,
): BootCosmetics {
  if (prev && prev.tileSkin === next.tileSkin && prev.tableSkin === next.tableSkin) return prev;
  return next;
}

export function resolveEffectiveCosmetics(input: ResolveCosmeticsInput): EffectiveCosmetics {
  const usePersonal = !input.isHost && input.appearanceMode === 'personal';
  if (usePersonal) {
    return {
      tableSkin: normalizeTableSkin(input.selectedTableSkin),
      tileSkin: normalizeTileSkin(input.selectedTileSkin),
    };
  }
  return {
    tableSkin: normalizeTableSkin(input.hostTableSkin),
    tileSkin: normalizeTileSkin(input.hostTileSkin),
  };
}
